import type { FlatConfig } from './flatConfig';
import type {
  AfaStorageBlock, HybridStorageBlock, HybridFlatFields, AfaFlatFields,
} from './StorageBlock';
import {
  hybridBlockFromFlat, hybridBlockToFlat,
  afaBlockFromFlat, afaBlockToFlat,
} from './StorageBlock';

export interface DirectorConfig {
  veloCount: number;
  veloSsdCapacity: number;
}

export interface SystemConfiguration {
  systemType: string;
  interfaceOption: string;
  networkProtocol: string;
  rackSize: number;
  sizeByNodeCount: boolean;
  hardwareVendor: string;
  compressionRatio: number;      // legacy system-level; slated for removal (spec §4.1)
  endToEndEncryption: boolean;   // legacy system-level; slated for removal (spec §4.1)
  director: DirectorConfig;
  hybrid: HybridStorageBlock;
  afa: AfaStorageBlock;
}

export function systemConfigFromFlat(c: FlatConfig): SystemConfiguration {
  return {
    systemType: c.systemType,
    interfaceOption: c.interfaceOption,
    networkProtocol: c.networkProtocol,
    rackSize: c.rackSize,
    sizeByNodeCount: c.sizeByNodeCount,
    hardwareVendor: c.hardwareVendor,
    compressionRatio: c.compressionRatio,
    endToEndEncryption: c.endToEndEncryption,
    director: { veloCount: c.veloCount, veloSsdCapacity: c.veloSsdCapacity },
    hybrid: hybridBlockFromFlat(c),
    afa: afaBlockFromFlat(c),
  };
}

// The exact FlatConfig keys this box owns: system-level + director scalars plus
// the hybrid/afa block fields. Composed from the block field types so the set
// stays in sync automatically. Returning this (not Partial<FlatConfig>) lets the
// compiler enforce total coverage in toFlatConfig.
export type SystemFlatFields = Pick<
  FlatConfig,
  | 'systemType'
  | 'interfaceOption'
  | 'networkProtocol'
  | 'rackSize'
  | 'sizeByNodeCount'
  | 'hardwareVendor'
  | 'compressionRatio'
  | 'endToEndEncryption'
  | 'veloCount'
  | 'veloSsdCapacity'
> & HybridFlatFields & AfaFlatFields;

export function systemConfigToFlat(s: SystemConfiguration): SystemFlatFields {
  return {
    systemType: s.systemType,
    interfaceOption: s.interfaceOption,
    networkProtocol: s.networkProtocol,
    rackSize: s.rackSize,
    sizeByNodeCount: s.sizeByNodeCount,
    hardwareVendor: s.hardwareVendor,
    compressionRatio: s.compressionRatio,
    endToEndEncryption: s.endToEndEncryption,
    veloCount: s.director.veloCount,
    veloSsdCapacity: s.director.veloSsdCapacity,
    ...hybridBlockToFlat(s.hybrid),
    ...afaBlockToFlat(s.afa),
  };
}

// Alias for test clarity
export const toSystemConfiguration = systemConfigFromFlat;
